import { useQuery } from '@tanstack/react-query';
import { eachDayOfInterval, endOfDay, format, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { QueryKey } from '@/Config/api/queryKey';
// Local Statistics interface for conversation analytics
interface Statistics {
  conversation_analysis_stats?: {
    conversation_outcome?: Record<string, Record<string, number>>;
    sentiment_analysis?: Record<string, Record<string, number>>;
    conversation_topic?: Record<string, Record<string, number>>;
    open_ended_topic?: Record<string, Record<string, number>>;
  };
}

// Local copy for clarity
type TimeSeriesCategory = Record<string, Record<string, number>>;

// Legacy 2-label outcome
export const LEGACY_OUTCOME_LABELS = ['Unresolved', 'Resolved'] as const;
// Unified 4-label outcome
export const UNIFIED_OUTCOME_LABELS = [
  'Unresolved',
  'Small Talk',
  'Assumed Resolution',
  'Confirmed Resolution',
] as const;

export const SENTIMENT_LABELS = ['Neutral', 'Positive', 'Negative'] as const;

// Sanitize keys for chart data keys & CSS variables
export const toKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const toISODate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

export function detectOutcomeLabels(raw: TimeSeriesCategory | undefined): readonly string[] {
  if (!raw) return LEGACY_OUTCOME_LABELS;
  for (const daily of Object.values(raw)) {
    if (daily && 'Confirmed Resolution' in daily) {
      return UNIFIED_OUTCOME_LABELS;
    }
  }
  return LEGACY_OUTCOME_LABELS;
}

export function buildStackedSeries(
  seriesByDate: TimeSeriesCategory | undefined,
  startDate: Date,
  endDate: Date,
  orderedLabels: readonly string[],
) {
  if (!seriesByDate) return [] as Array<Record<string, number | string>>;

  const days = eachDayOfInterval({ start: startOfDay(startDate), end: endOfDay(endDate) });

  return days.map((d) => {
    const date = toISODate(d);
    const row: Record<string, number | string> = { date };
    const daily = seriesByDate[date] || {};

    for (const label of orderedLabels) {
      const key = toKey(label);
      row[key] = daily[label] ?? 0;
    }

    return row;
  });
}

export function orderLabelsByTotal(seriesByDate: TimeSeriesCategory | undefined) {
  if (!seriesByDate) return [] as string[];
  const totals: Record<string, number> = {};
  for (const daily of Object.values(seriesByDate)) {
    for (const [label, count] of Object.entries(daily || {})) {
      totals[label] = (totals[label] || 0) + (count || 0);
    }
  }
  return Object.keys(totals).sort((a, b) => (totals[b] ?? 0) - (totals[a] ?? 0));
}

export const useConversationAnalytics = ({
  startDate,
  endDate,
}: {
  startDate: Date;
  endDate: Date;
}) => {
  const { data, isLoading, isError } = useQuery<Statistics>({
    queryKey: [
      QueryKey.analytics.general,
      { start: toISODate(startDate), end: toISODate(endDate) },
    ],
  });

  // Detect unified (4-label) vs legacy (2-label) outcome data
  const outcomeLabels = useMemo(
    () => detectOutcomeLabels(data?.conversation_analysis_stats?.conversation_outcome),
    [data?.conversation_analysis_stats?.conversation_outcome],
  );

  const outcomeData = useMemo(
    () =>
      buildStackedSeries(
        data?.conversation_analysis_stats?.conversation_outcome,
        startDate,
        endDate,
        outcomeLabels,
      ),
    [data?.conversation_analysis_stats?.conversation_outcome, startDate, endDate, outcomeLabels],
  );

  const sentimentData = useMemo(
    () =>
      buildStackedSeries(
        data?.conversation_analysis_stats?.sentiment_analysis,
        startDate,
        endDate,
        SENTIMENT_LABELS,
      ),
    [data?.conversation_analysis_stats?.sentiment_analysis, startDate, endDate],
  );

  // Topic series (dynamic labels)
  const topicLabels = useMemo(
    () => orderLabelsByTotal(data?.conversation_analysis_stats?.conversation_topic),
    [data?.conversation_analysis_stats?.conversation_topic],
  );

  const topicData = useMemo(
    () =>
      buildStackedSeries(
        data?.conversation_analysis_stats?.conversation_topic,
        startDate,
        endDate,
        topicLabels,
      ),
    [data?.conversation_analysis_stats?.conversation_topic, startDate, endDate, topicLabels],
  );

  const topicConfig = useMemo(() => {
    const palette = [
      'var(--chart-1)',
      'var(--chart-2)',
      'var(--chart-3)',
      'var(--chart-4)',
      'var(--chart-5)',
      'var(--chart-6)',
      'var(--chart-7)',
      'var(--chart-8)',
      'var(--chart-9)',
      'var(--chart-10)',
      'var(--chart-11)',
      'var(--chart-12)',
      'var(--chart-13)',
      'var(--chart-14)',
      'var(--chart-15)',
    ];
    return topicLabels.reduce(
      (acc, label, idx) => {
        acc[toKey(label)] = { label, color: palette[idx % palette.length] };
        return acc;
      },
      {} as Record<string, { label: string; color: string }>,
    );
  }, [topicLabels]);

  const topicTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const row of topicData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === 'date') continue;
        if (typeof v === 'number') totals[k] = (totals[k] || 0) + v;
      }
    }
    return totals;
  }, [topicData]);

  const outcomeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const row of outcomeData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === 'date') continue;
        if (typeof v === 'number') totals[k] = (totals[k] || 0) + v;
      }
    }
    return totals;
  }, [outcomeData]);

  // Open-ended topic series (from unified analytics, dynamic labels — top 10 only)
  const MAX_OPEN_ENDED_TOPICS = 10;
  const openEndedTopicLabels = useMemo(
    () =>
      orderLabelsByTotal(data?.conversation_analysis_stats?.open_ended_topic).slice(
        0,
        MAX_OPEN_ENDED_TOPICS,
      ),
    [data?.conversation_analysis_stats?.open_ended_topic],
  );

  const openEndedTopicData = useMemo(
    () =>
      buildStackedSeries(
        data?.conversation_analysis_stats?.open_ended_topic,
        startDate,
        endDate,
        openEndedTopicLabels,
      ),
    [data?.conversation_analysis_stats?.open_ended_topic, startDate, endDate, openEndedTopicLabels],
  );

  const openEndedTopicConfig = useMemo(() => {
    const palette = [
      'var(--chart-1)',
      'var(--chart-2)',
      'var(--chart-3)',
      'var(--chart-4)',
      'var(--chart-5)',
      'var(--chart-6)',
      'var(--chart-7)',
      'var(--chart-8)',
      'var(--chart-9)',
      'var(--chart-10)',
      'var(--chart-11)',
      'var(--chart-12)',
      'var(--chart-13)',
      'var(--chart-14)',
      'var(--chart-15)',
    ];
    return openEndedTopicLabels.reduce(
      (acc, label, idx) => {
        acc[toKey(label)] = { label, color: palette[idx % palette.length] };
        return acc;
      },
      {} as Record<string, { label: string; color: string }>,
    );
  }, [openEndedTopicLabels]);

  const openEndedTopicTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const row of openEndedTopicData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === 'date') continue;
        if (typeof v === 'number') totals[k] = (totals[k] || 0) + v;
      }
    }
    return totals;
  }, [openEndedTopicData]);

  return {
    outcomeData,
    outcomeLabels,
    outcomeTotals,
    sentimentData,
    topicData,
    topicConfig,
    topicTotals,
    openEndedTopicData,
    openEndedTopicConfig,
    openEndedTopicTotals,
    isLoading,
    isError,
  } as const;
};
